import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { logger } from '../utils/logger';
import { API } from '../utils/apiBase';

const AuthContext = createContext(null);


export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const apiRef = useRef(API);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${apiRef.current}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (err) {
      logger.error('Auth check failed:', err.message);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(
      `${apiRef.current}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    // Funnel Analytics Phase 2 — pass the anonymous visitor cookie so the
    // backend can bridge prior free-ATS funnel events to this new user.
    let visitor_id = null;
    try { visitor_id = window.localStorage.getItem('ascend_visitor_id') || null; } catch (_e) { /* noop */ }
    const response = await axios.post(
      `${apiRef.current}/auth/register`,
      { email, password, name, visitor_id },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${apiRef.current}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      logger.error('Logout failed:', err.message);
    }
    setUser(null);
  }, []);

  const googleLogin = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const processGoogleSession = useCallback(async (sessionId) => {
    const response = await axios.post(
      `${apiRef.current}/auth/google/session`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  }, []);

  const refreshAuth = useCallback(async () => {
    await checkAuth();
  }, [checkAuth]);

  const contextValue = useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    googleLogin,
    processGoogleSession,
    refreshAuth,
    isAuthenticated: !!user,
  }), [user, loading, login, register, logout, googleLogin, processGoogleSession, refreshAuth]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
